import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import React, { useEffect, useRef } from 'react';

import ContentfulImage from '../../Common/ContentfulImage';
import RichTextConverter from '../../Common/RichText/RichTextConverter';
import PageSection from '../../PageSections/PageSection';
import * as styles from './BehavioralData.module.scss';
import BehavioralDataSvg from './BehavioralDataSvg';
import IntegrationsSvg from './IntegrationsSvg';
import SessionReplaySvg from './SessionReplaySvg';

gsap.registerPlugin(ScrollTrigger);
gsap.defaults({
  duration: 0.1,
});

export default function BehavioralData({ content }) {
  const trigger = useRef();
  const tl = useRef();

  const animations = [<IntegrationsSvg />, <BehavioralDataSvg />, <SessionReplaySvg />];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set('.full', {
        xPercent: -110,
      });

      gsap.set(['#background-1', '#section-1'], {
        opacity: 1,
      });

      gsap.set(['#section-2', '#section-3'], {
        visibility: 'hidden',
      });

      tl.current = gsap
        .timeline({
          scrollTrigger: {
            trigger: trigger.current,
            start: () => 'top 5%',
            end: () => '+=' + trigger.current.clientHeight * 0.75,
            scrub: 2,
          },
        })
        .to('#progress-1', {
          xPercent: 0,
        })
        .to(
          ['#inner-ring', '#outer-ring', '#inner-dots', '#outer-dots'],
          {
            opacity: 1,
            transform: 'scale(1)',
            stagger: 0.05,
            duration: 0.25,
          },
          '<'
        )
        .to('#logos > *', { opacity: 1, transform: 'scale(1)', stagger: 0.05, duration: 0.1 }, '<+=0.25')
        .to('#section-1', { opacity: 0, duration: 0.1 }, '<+=0.4')
        .to('#background-2', {
          opacity: 1,
          duration: 0.1,
        })
        .to('#section-1', { visibility: 'hidden', duration: 0 }, '<')
        .to('#section-2', { visibility: 'visible', duration: 0 }, '<')
        .to('#section-2', { opacity: 1, duration: 0.1 }, '<')
        .to(
          '#progress-2',
          {
            xPercent: 0,
          },
          '<'
        )
        .to(
          '#sentiments > g',
          {
            opacity: 1,
            transform: 'scale(1)',
            stagger: 0.1,
            duration: 0.05,
          },
          '<'
        )
        .to('#section-2', { opacity: 0, duration: 0.1 }, '<+=0.4')
        .to('#background-3', {
          opacity: 1,
          duration: 0.1,
        })
        .to('#section-2', { visibility: 'hidden', duration: 0 }, '<')
        .to('#section-3', { visibility: 'visible', duration: 0 }, '<')
        .to('#section-3', { opacity: 1, duration: 0.1 }, '<')
        .to(
          '#progress-3',
          {
            xPercent: 0,
          },
          '<'
        )
        .to(
          '#pop-out',
          {
            opacity: 1,
            transform: 'none',
            duration: 0.05,
          },
          '<'
        )
        .to(
          '#progress',
          {
            xPercent: 7000,
            ease: 'linear',
          },
          '<'
        )
        .to(
          '#steps-active > g',
          {
            opacity: 1,
            duration: 0,
            stagger: 0.05,
            ease: 'none',
          },
          '<'
        )
        .to(
          '#steps > g',
          {
            opacity: 0,
            duration: 0,
            stagger: 0.05,
            ease: 'none',
          },
          '<'
        )
        .to(
          '#steps-active > g',
          {
            opacity: 0,
            duration: 0,
            stagger: 0.05,
            ease: 'none',
          },
          '<+=0.05'
        )
        .to(
          '#steps > g',
          {
            opacity: 1,
            duration: 0,
            stagger: 0.05,
            ease: 'none',
          },
          '<'
        );
    }, trigger);

    return () => ctx.revert();
  }, [trigger]);

  // Function to create section for mobile
  const createMobileSection = (section) => (
    <PageSection className={styles.mobileCard} key={section.id}>
      <ContentfulImage imageFragment={section.mainImage} lazy={true} className={styles.image} />
      <h2>{section.title}</h2>
      <RichTextConverter className={styles.copy} copy={section.copy} />
    </PageSection>
  );

  // Function to create section for Desktop
  const createDesktopSection = (section, i) => (
    <div id={`section-${i + 1}`} className={styles.grid} key={section.id}>
      <div className={styles.copy}>
        <h2>{section.title}</h2>
        <RichTextConverter copy={section.copy} />
      </div>
      {animations[i]}
    </div>
  );

  return (
    <>
      <div className={styles.mobile} data-analytics-section={'behavior-data-animation-mobile'}>
        {content.map(createMobileSection)}
      </div>
      <div className={styles.desktop} ref={trigger} data-analytics-section={'behavior-data-animation-desktop'}>
        {content.map((_, i) => (
          <div id={`background-${i + 1}`} className={styles.background} key={`background-${i + 1}`} />
        ))}
        <PageSection slim className={styles.section}>
          <div id="progress-bars" className={styles.progressBars}>
            {[...Array(3)].map((_, i) => (
              <div className="progress-bar" key={i}>
                <span className={styles.empty} />
                <span id={`progress-${i + 1}`} className={['full', styles.full].join(' ')} />
              </div>
            ))}
          </div>
          <div className={styles.content}>{content.map(createDesktopSection)}</div>
        </PageSection>
      </div>
    </>
  );
}
